import React, { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useForm } from "react-hook-form";
import { X } from "lucide-react";

interface EditVehicleModalProps {
  vehicle: any;
  isOpen: boolean;
  onClose: () => void;
  onSave: (vehicleId: string, data: any) => void;
}

export default function EditVehicleModal({
  vehicle,
  isOpen,
  onClose,
  onSave,
}: EditVehicleModalProps) {
  const { register, handleSubmit, reset, setValue } = useForm({
    defaultValues: {
      driver: "",
      model: "",
      year: 2020,
      status: "Inactive",
      type: "Dry Van",
      maxCapacity: 3400,
      maxWeight: 80000,
    },
  });

  // Update form values when vehicle prop changes
  useEffect(() => {
    if (vehicle && isOpen) {
      setValue("driver", vehicle.driver || "");
      setValue("model", vehicle.model || "");
      setValue("year", vehicle.year || 2020);
      setValue("status", vehicle.status || "Inactive");
      setValue("type", vehicle.type || "Dry Van");
      setValue("maxCapacity", vehicle.maxCapacity || 3400);
      setValue("maxWeight", vehicle.maxWeight || 80000);
    }
  }, [vehicle, isOpen, setValue]);

  const onSubmit = (data: any) => {
    onSave(vehicle.id, data);
    onClose();
    reset();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold">Edit Vehicle</h2>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Driver</label>
            <Input
              type="text"
              {...register("driver")}
              placeholder="Driver name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Model</label>
            <Input
              type="text"
              {...register("model", { required: true })}
              placeholder="Vehicle model"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Year</label>
            <Input
              type="number"
              {...register("year", { required: true })}
              placeholder="Vehicle year"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Status</label>
            <select
              {...register("status", { required: true })}
              className="w-full p-2 border border-gray-300 rounded-md"
            >
              <option value="Active">Active</option>
              <option value="Loading">Loading</option>
              <option value="Maintenance">Maintenance</option>
              <option value="Inactive">Inactive</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Type</label>
            <select
              {...register("type", { required: true })}
              className="w-full p-2 border border-gray-300 rounded-md"
            >
              <option value="Flatbed">Flatbed</option>
              <option value="Dry Van">Dry Van</option>
              <option value="Reefer">Reefer</option>
              <option value="Box Truck">Box Truck</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">
              Max Capacity
            </label>
            <Input
              type="number"
              {...register("maxCapacity", { required: true })}
              placeholder="Max capacity"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Max Weight</label>
            <Input
              type="number"
              {...register("maxWeight", { required: true })}
              placeholder="Max weight"
            />
          </div>

          <div className="flex space-x-2 pt-4">
            <Button type="submit" className="flex-1">
              Save Changes
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1"
            >
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}